import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { SITE } from "@/data";

export const Navbar = () => {
  const { t, lang, toggle } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t.nav.services, href: "#services" },
    { label: t.nav.destinations, href: "#destinations" },
    { label: t.nav.packages, href: "#packages" },
    { label: t.nav.pricing, href: "#pricing" },
    { label: t.nav.contact, href: "#contact" },
  ];

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed w-full z-50 backdrop-blur-xl border-b transition-all duration-300 ${
        scrolled ? "bg-sand/80 border-border shadow-sm" : "bg-sand/40 border-transparent"
      }`}
      data-testid="main-header"
    >
      <div className="px-6 md:px-12 lg:px-20 mx-auto max-w-7xl flex items-center justify-between h-20">
        <button onClick={() => go("#hero")} data-testid="logo-home-button">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="font-body text-sm text-jungle hover:text-teal transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle-button"
            className="flex items-center gap-1.5 font-body text-sm text-jungle hover:text-teal transition-colors border border-border px-3 py-1.5"
          >
            <Globe size={15} />
            {lang === "es" ? "EN" : "ES"}
          </button>
          <button
            onClick={() => go("#book")}
            data-testid="nav-book-button"
            className="hidden sm:inline-flex bg-teal text-sand hover:bg-teal-dark font-body text-sm px-6 py-2.5 transition-colors"
          >
            {t.nav.book}
          </button>
          <button
            className="lg:hidden text-jungle"
            onClick={() => setOpen((p) => !p)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-sand border-t border-border px-6 py-4 flex flex-col gap-3" data-testid="mobile-menu">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="font-body text-left text-jungle py-2 hover:text-teal"
              data-testid={`mobile-nav-${l.href.replace("#", "")}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("#book")}
            className="bg-teal text-sand font-body px-6 py-3 mt-2"
            data-testid="mobile-book-button"
          >
            {t.nav.book}
          </button>
        </div>
      )}
    </header>
  );
};
